import { useEffect, useState } from 'react';

import { client, useErrorNotifications } from 'core/http';

import usePagination, { PaginationResponse } from './usePagination';

type Params = object & { cursor?: string };

// Instead of setting params to {}, always use the same
// object instance as {} creates a new (different from previous) object.
const empty = {};

export default function usePaginationHandler<T>(
  url?: string,
  searchParams?: object
) {
  const showErrorNotification = useErrorNotifications();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<T[]>([]);
  const [params, setParams] = useState<Params>(searchParams || empty);
  const { setResponse, cursor, loadMore, hasMore } = usePagination();

  // Update the cursor param on change
  useEffect(() => {
    cursor && setParams(params => ({ ...params, next_cursor: cursor }));
  }, [cursor]);
  // Clear the params and set new given search params (without the prev cursor)
  useEffect(() => setParams(searchParams || empty), [searchParams]);
  // Clear current set of data
  useEffect(() => setData([]), [searchParams]);
  // And remove the cached previous response from the pagination
  useEffect(() => setResponse(undefined), [searchParams, setResponse]);

  useEffect(() => {
    if (url === undefined) {
      return;
    }

    setLoading(true);

    client()
      .get<PaginationResponse<T>>(url, { params })
      .then(response => response.data)
      .then(response => {
        setResponse(response);
        setData(data => [...data, ...response.data]);
      })
      .catch(showErrorNotification)
      .finally(() => setLoading(false));
  }, [params, setResponse, showErrorNotification, url]);

  return { data, loading, loadMore, hasMore };
}
